import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { Section, SectionHeading } from '../components/ui/Section';
import { Prose, ProseParagraph, ProseList } from '../components/ui/Prose';
import { PullQuote, SectionDivider } from '../components/ui/PullQuote';
import { ContinueExploring } from '../components/ui/ContinueExploring';

const TheCategory = () => {
  return (
    <div>
      <PageHeader 
        title="The Category"
        subtitle="Why a new operating layer is required"
      />

      {/* The pattern humanity keeps repeating */}
      <Section>
        <SectionHeading>The pattern humanity keeps repeating</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Across history, civilizations have built powerful systems—economic, political, technological, educational. Each was optimized for success as understood at the time but eventually produced consequences outliving its builders' intentions.
          </ProseParagraph>
          
          <ProseParagraph>
            The limitation was not ambition, but optimization ungoverned by consequence.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* The missing generative layer */}
      <Section>
        <SectionHeading>The missing generative layer</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Modern systems are exceptionally good at:
          </ProseParagraph>
          
          <ProseList items={[
            "execution",
            "scale",
            "acceleration"
          ]} />
          
          <ProseParagraph>
            They largely lack a governing layer that shapes what those capabilities are allowed to generate once they succeed.
          </ProseParagraph>
          
          <ProseParagraph>
            Most systems implicitly assume:
          </ProseParagraph>
          
          <ProseList items={[
            "the definition of success is already correct",
            "faster and larger is inherently better",
            "unintended consequences can be addressed later"
          ]} />
          
          <ProseParagraph>
            These assumptions appear manageable at small scale but become destabilizing at global scale.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why existing approaches are insufficient */}
      <Section>
        <SectionHeading>Why existing approaches are insufficient</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Several approaches attempt to address this gap but don't resolve it structurally.
          </ProseParagraph>
          
          <div className="space-y-4 mt-6">
            <ProseParagraph>
              <strong>Ethics frameworks</strong> advise, but do not govern execution.
            </ProseParagraph>
            
            <ProseParagraph>
              <strong>Compliance systems</strong> react after harm has occurred.
            </ProseParagraph>
            
            <ProseParagraph>
              <strong>ESG and reporting regimes</strong> measure impact after the fact.
            </ProseParagraph>
            
            <ProseParagraph>
              <strong>Mission statements</strong> express intent without enforcing consequence.
            </ProseParagraph>
          </div>
          
          <ProseParagraph className="mt-6">
            These approaches sit <strong>around</strong> the system, not <strong>within</strong> it.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What defines this category */}
      <Section>
        <SectionHeading>What defines this category</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS defines a category that is not:
          </ProseParagraph>
          
          <ProseList items={[
            "governance as oversight",
            "ethics as aspiration",
            "sustainability as reporting",
            "purpose as branding"
          ]} />
          
          <ProseParagraph>
            This category is:
          </ProseParagraph>
          
          <ProseParagraph>
            <strong>A governing operating layer that constrains what systems are allowed to optimize for—before scale compounds consequence.</strong>
          </ProseParagraph>
          
          <ProseParagraph>
            This category exists at the <strong>generative layer</strong> where:
          </ProseParagraph>
          
          <ProseList items={[
            "direction is specified",
            "success is defined",
            "metrics are qualified",
            "incentives are aligned",
            "autonomy is granted safely"
          ]} />
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why this category could not emerge earlier */}
      <Section>
        <SectionHeading>Why this category could not emerge earlier</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            This category becomes necessary only when three conditions converge:
          </ProseParagraph>
          
          <div className="space-y-6 mt-6">
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">1. Systems reach planetary scale</h4>
              <ProseParagraph className="!mb-0">
                Consequences now propagate globally and across generations. Optimization becomes autonomous.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">2. AI and algorithmic systems accelerate outcomes</h4>
              <ProseParagraph className="!mb-0">
                Faster than human governance can react.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">3. Mis-specified success becomes existential</h4>
              <ProseParagraph className="!mb-0">
                The cost of "winning the wrong way" is no longer localized but civilizational.
              </ProseParagraph>
            </div>
          </div>
          
          <ProseParagraph className="mt-8">
            Only now does the absence of a governing layer become visible as the root problem.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why this category is inevitable now */}
      <Section>
        <SectionHeading>Why this category is inevitable now</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            As systems gain the power to shape civilization by default, governing direction can no longer remain implicit. This is not due to imminent failure but because a new level of capability has emerged.
          </ProseParagraph>
          
          <ProseParagraph>
            For the first time, systems are capable of:
          </ProseParagraph>
          
          <ProseList items={[
            "generating futures at planetary scale",
            "translating intent into consequence autonomously",
            "compounding direction faster than culture, policy, or hindsight can intervene"
          ]} />
          
          <ProseParagraph>
            At this level of power, <strong>direction itself becomes a design requirement</strong>.
          </ProseParagraph>
          
          <ProseParagraph>
            A generative operating layer becomes inevitable when systems are no longer merely tools but world-producing engines.
          </ProseParagraph>
          
          <ProseParagraph>
            This category exists because it is now possible to:
          </ProseParagraph>
          
          <ProseList items={[
            "govern systems before optimization begins",
            "constrain what kinds of futures can be generated",
            "make certain outcomes structurally inevitable rather than aspirational"
          ]} />
          
          <ProseParagraph>
            The category is not a response to collapse but the infrastructure required for <strong>deliberate civilization-scale creation</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What differentiates this category from adjacent fields */}
      <Section>
        <SectionHeading>What differentiates this category from adjacent fields</SectionHeading>
        
        <Prose>
          {/* Comparison table rendered as structured list */}
          <div className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-b border-border">
              <div className="font-medium text-muted-foreground text-sm uppercase tracking-wider">Adjacent Field</div>
              <div className="font-medium text-muted-foreground text-sm uppercase tracking-wider">What It Does</div>
              <div className="font-medium text-muted-foreground text-sm uppercase tracking-wider">What This Category Does</div>
            </div>
            
            {[
              { field: 'Ethics', does: 'Advises behavior', category: 'Governs permissible outcomes' },
              { field: 'ESG', does: 'Reports impact', category: 'Prevents incoherent impact' },
              { field: 'Mission', does: 'Expresses intent', category: 'Enforces consequence' },
              { field: 'Strategy', does: 'Sets goals', category: 'Qualifies what goals may exist' },
              { field: 'Governance', does: 'Oversees execution', category: 'Defines the conditions for execution' },
              { field: 'Optimization', does: 'Maximizes metrics', category: 'Constrains what may be optimized' },
            ].map((row, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-b border-border/50">
                <div className="font-medium">{row.field}</div>
                <div className="text-muted-foreground">{row.does}</div>
                <div>{row.category}</div>
              </div>
            ))}
          </div>
          
          <ProseParagraph className="mt-8">
            This category does not replace these fields; it <strong>renders them coherent</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why Coherence OS occupies this category */}
      <Section>
        <SectionHeading>Why Coherence OS occupies this category</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is not an application of the category; it is the <strong>first complete instantiation of it</strong>.
          </ProseParagraph>
          
          <ProseParagraph>
            It operates:
          </ProseParagraph>
          
          <ProseList items={[
            "above metrics",
            "prior to optimization",
            "across time horizons",
            "within civilizational constraints"
          ]} />
          
          <ProseParagraph>
            It is designed for organizations, companies, and systems that understand that power without direction is no longer survivable.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What becomes possible when systems are governed at the generative layer */}
      <Section>
        <SectionHeading>What becomes possible when systems are governed at the generative layer</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            When the systems that generate outcomes are governed before optimization, entirely new classes of futures become structurally achievable:
          </ProseParagraph>
          
          <ProseList items={[
            "innovation accelerates without erosion",
            "AI scales without governance collapse",
            "organizations, companies, institutions, and systems grow without legitimacy loss",
            "performance compounds without depletion"
          ]} />
          
          <ProseParagraph>
            This leads to:
          </ProseParagraph>
          
          <ProseList items={[
            "progress becomes durable",
            "scale becomes stabilizing",
            "systems become worthy of trust again"
          ]} />
        </Prose>
        
        {/* Continue exploring */}
        <ContinueExploring 
          links={[
            { label: 'What Is Coherence OS', href: '/what-is-coherence-os' },
            { label: 'Coherence Metrics', href: '/coherence-metrics' },
            { label: 'For Investors', href: '/for-investors' },
          ]}
        />
      </Section>
    </div>
  );
};

export default TheCategory;
